// ==============================================================================
// FILE: /src/pages/BulkIngestPage.tsx (UPDATED LAYOUT)
// PURPOSE: A new page for uploading a master ZIP file for bulk processing.
// NOTE: This version is updated to match the user's proposed two-column design.
// ==============================================================================
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Grid,
  Alert,
  LinearProgress,
  Link,
  Divider,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  CheckCircleOutline as CheckCircleIcon,
  FolderZipOutlined as FolderZipIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useAppContext, Page } from '../contexts/AppContext';
import { GoogleIcon } from '../components/GoogleIcon'; // Assuming a simple SVG icon component

// Helper to format file size for display
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export default function BulkIngestPage(): JSX.Element {
  const { apiService, setActivePage, showNotification } = useAppContext();
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setZipFile(acceptedFiles[0]);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/zip': ['.zip'] },
    multiple: false,
  });

  const handleSubmit = async () => {
    if (!zipFile) {
      showNotification('Please select a ZIP file to upload.', 'warning');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setUploadProgress(0);

    try {
      const signedUrlResponse = await apiService.getBulkSignedUrl(zipFile.name);
      const { url } = signedUrlResponse;

      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      xhr.setRequestHeader('Content-Type', 'application/zip');

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          setUploadProgress(percentComplete);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          showNotification('Bulk job submitted successfully! Processing has started.', 'success');
          setActivePage('Bulk Job Status');
        } else {
          setError(`Upload failed with status: ${xhr.status}. Please try again.`);
          setIsSubmitting(false);
        }
      };

      xhr.onerror = () => {
        setError('An error occurred during the upload. Please check your network connection.');
        setIsSubmitting(false);
      };

      xhr.send(zipFile);

    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred while preparing the upload.');
      setIsSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={3}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Bulk Claim Ingestion
        </Typography>
        <Typography color="text.secondary">
          Process an entire day's worth of claims in a single, efficient action.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left Column: Upload Options */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Option A: Upload a ZIP File</Typography>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'background.default',
              transition: 'border-color 0.3s, background-color 0.3s',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
            }}
          >
            <input {...getInputProps()} />
            <UploadFileIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
            <Typography variant="h6" color="text.primary">
              Drag & drop your master ZIP file here
            </Typography>
            <Typography color="text.secondary">or <Link component="span" underline="hover">click to browse</Link></Typography>
            <Typography variant="caption" color="text.secondary" sx={{mt: 1}}>Max file size: 2GB</Typography>
          </Box>
          
          <Divider sx={{ my: 3 }}>OR</Divider>

          <Typography variant="h6" gutterBottom>Option B: Process from Google Drive</Typography>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            disabled // Disabled until the feature is implemented
            sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
          >
            Select from Google Drive
          </Button>
        </Grid>

        {/* Right Column: Instructions */}
        <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.100', height: '100%' }}>
                <Typography variant="h6" gutterBottom>How to Structure Your ZIP File</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    For a successful bulk upload, please ensure your ZIP file is organized correctly. Create one main folder for each employee's claim.
                </Typography>
                <Box sx={{ p: 2, backgroundColor: '#2d2d2d', color: '#f5f5f5', borderRadius: 1, fontFamily: 'monospace' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <FolderZipIcon sx={{ mr: 1, color: '#ffd54f' }} />
                        Bulk_Claims_Aug_10.zip
                    </Box>
                    <Box sx={{ pl: 2, borderLeft: '1px solid #555' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        ├─ Employee_John_Doe/
                        </Typography>
                        <Box sx={{ pl: 4, borderLeft: '1px solid #555', ml: '6px' }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>├─ claim_sheet.xlsx</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>├─ receipt1.pdf</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>└─ receipt2.jpg</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        ├─ Employee_Jane_Smith/
                        </Typography>
                        <Box sx={{ pl: 4, borderLeft: '1px solid #555', ml: '6px' }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>├─ claim_sheet.xlsx</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>└─ receipt_for_lunch.pdf</Typography>
                        </Box>
                         <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        └─ ... and so on
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Grid>
      </Grid>
      
      {zipFile && !isSubmitting && (
        <Box sx={{ mt: 3 }}>
            <Alert severity="success" action={
                <Button color="inherit" size="small" onClick={handleSubmit}>
                    SUBMIT
                </Button>
            }>
                Ready to upload: <strong>{zipFile.name}</strong> ({formatBytes(zipFile.size)})
            </Alert>
        </Box>
      )}

      {isSubmitting && (
        <Box sx={{ width: '100%', mt: 3 }}>
          <Typography sx={{ mb: 1 }}>Uploading...</Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
}
